import { useEffect, useState } from 'react';
import { Footer, Header, Link } from '../components/Brand.tsx';
import { followApi, type UnsubInfo } from '../lib/follow.ts';

/**
 * The unsubscribe link in an alert email or text. One tap: the alert is off as soon as the page
 * opens (no sign-in, the link is signed), with Undo in case it was a mistake.
 */
export function Unsubscribe({ token }: { token: string }) {
  const [info, setInfo] = useState<UnsubInfo | 'working' | 'invalid'>('working');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void followApi.unsubscribe(token).then(setInfo, () => setInfo('invalid'));
  }, [token]);

  const toggle = async (undo: boolean) => {
    setBusy(true);
    await followApi.unsubscribe(token, undo).then(setInfo, () => undefined);
    setBusy(false);
  };

  let title = 'One moment…';
  let detail: string | null = null;
  if (info === 'invalid') {
    title = 'This link doesn’t work';
    detail = 'It may have been copied wrong. You can manage your alerts from your account.';
  } else if (info !== 'working') {
    const what = info.kind === 'channel' ? `alerts from ${info.channel ?? 'this channel'}` : info.kind === 'email' ? 'alert emails' : 'alert texts';
    title = info.on ? `${cap(what)} are back on` : `No more ${what}`;
    detail = info.on
      ? 'We’ll tell you again when they go live.'
      : info.kind === 'channel'
        ? `You still follow ${info.channel ?? 'them'}, you just won’t be told when they go live.`
        : info.kind === 'email'
          ? 'We won’t email you when channels you follow go live.'
          : 'We won’t text you when channels you follow go live.';
  }

  return (
    <main className="flex min-h-full flex-col bg-paper">
      <Header tone="paper" />
      <div className="mx-auto w-full max-w-md flex-1 px-5 pt-10 pb-20 sm:pt-16">
        <span className="speed-stripes" aria-hidden />
        <h1 className="speed-text mt-4 text-[34px] leading-[1.05]">{title}</h1>
        {detail && <p className="mt-3 text-[17px] leading-relaxed text-muted">{detail}</p>}
        {typeof info === 'object' && (
          <button type="button" disabled={busy} onClick={() => void toggle(!info.on)} className="mt-6 h-12 rounded-xl border-2 border-line bg-white px-5 font-extrabold hover:border-navy disabled:opacity-50">
            {info.on ? 'Turn them off again' : 'Undo'}
          </button>
        )}
        <Link to="/account" className="mt-6 block font-bold underline underline-offset-4">
          Manage your alerts
        </Link>
      </div>
      <Footer />
    </main>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
